/**
 * /chat — Slack-style conversational surface for the active team.
 *
 * The route does its own data loading (no loader) because the chat
 * picker drives a deep state machine — agent selection, optimistic
 * sessions, in-flight SSE streams. Pre-fetching agents in a loader
 * just delays the first paint without changing the UX.
 */

import {
  ArrowUp,
  Bold,
  Code,
  FileText,
  ImageIcon,
  Italic,
  Link as LinkIcon,
  List as ListIcon,
  Loader2,
  Paperclip,
  Smile,
  Square,
  X,
} from "lucide-react";
import {
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type JSX,
  useEffect,
  useRef,
  useState,
} from "react";

import { LetterAvatar } from "@/components/chat/avatar";
import { ChatMarkdown } from "@/components/chat/chat-markdown";
import { EmptyState, ErrorBanner } from "@/components/chat/error-banner";
import { PetCompanion } from "@/components/chat/pet-companion";
import {
  type ChatHistoryItem,
  type ChatSession,
  ChatSessionsRail,
} from "@/components/chat/sessions-rail";
import { ThinkingBlock } from "@/components/chat/thinking-block";
import { ToolCallList } from "@/components/chat/tool-call";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ChatPendingApprovalsBanner } from "@/features/approvals/chat-pending-banner";
import { api, apiUpload } from "@/lib/api";
import { consumeChatPreselectedAgentId } from "@/lib/chat-preselect";
import {
  type BubbleEvent,
  type ChatImage,
  type ChatPart,
  newAgentBubble,
  runTurn,
} from "@/lib/chat-stream";
import type { AgentListResponse, AgentSummary } from "@/lib/types";
import { cn } from "@/lib/utils";

import { useAuthState } from "../lib/auth";

interface ChatAttachment {
  id: number;
  name: string;
  size: number;
  bucket: "docs" | "imgs";
  source_path: string;
  absolute_path: string;
  part: ChatPart;
}

interface ChatUploadResponse {
  parts: ChatPart[];
  uploaded: Array<{
    source_path: string;
    absolute_path: string;
    bytes: number;
    bucket: "docs" | "imgs";
  }>;
}

type TranscriptEntry =
  | {
      id: number;
      kind: "user";
      text: string;
      speaker: string;
      createdAt: number;
      images?: ChatImage[];
    }
  | {
      id: number;
      kind: "agent";
      bubble: BubbleEvent;
      speaker: string;
      createdAt: number;
    }
  | { id: number; kind: "system"; text: string; createdAt: number };

const USER_SPEAKER_LABEL = "You";
const SESSION_RESET_COMMANDS = ["/new", "/clear"] as const;

function matchSessionResetCommand(text: string): string | undefined {
  const trimmed = text.trim().toLowerCase();
  return SESSION_RESET_COMMANDS.find((c) => c === trimmed);
}

function formatBubbleFooter(b: BubbleEvent): string {
  const parts: string[] = [];
  if (b.usage) parts.push(`${b.usage.totalTokens.toLocaleString()} tok`);
  if (typeof b.usage?.costUsd === "number") parts.push(`$${b.usage.costUsd.toFixed(4)}`);
  if (typeof b.elapsedMs === "number") {
    const s = b.elapsedMs / 1000;
    parts.push(s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);
  }
  return parts.join(" · ");
}

function imagesFromParts(parts: ChatPart[], fallbackTitle?: string): ChatImage[] {
  return parts
    .map((part): ChatImage | undefined => {
      if (part.type !== "image_url") return undefined;
      return {
        url: part.url,
        title: fallbackTitle,
        alt: fallbackTitle,
        source: "user",
      };
    })
    .filter((img): img is ChatImage => Boolean(img));
}

function ImageStrip({
  images,
  excludeAssistant,
}: {
  images?: ChatImage[];
  excludeAssistant?: boolean;
}): JSX.Element | null {
  if (!images || images.length === 0) return null;
  const visible = excludeAssistant ? images.filter((img) => img.source !== "assistant") : images;
  if (visible.length === 0) return null;
  return (
    <div className="mt-2 grid max-w-[520px] grid-cols-2 gap-2 sm:grid-cols-3">
      {visible.map((img, idx) => (
        <a
          key={`${img.url}-${idx}`}
          href={img.url}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-md border border-border bg-secondary/40"
          title={img.title ?? img.alt ?? "image"}
        >
          <img
            src={img.url}
            alt={img.alt ?? img.title ?? "image"}
            className="h-32 w-full object-contain"
            loading="lazy"
          />
        </a>
      ))}
    </div>
  );
}

function formatTime(value: number | string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function shortSession(id: string): string {
  return id.length <= 10 ? id : id.slice(0, 8);
}

function isBubbleEmpty(b: BubbleEvent): boolean {
  return (
    b.text === "" &&
    b.thinking === "" &&
    !b.thinkingActive &&
    b.toolCalls.length === 0 &&
    b.events.length === 0 &&
    b.images.length === 0 &&
    !b.reaction &&
    !b.errored
  );
}

function TranscriptSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3 py-2" aria-busy="true" aria-label="Loading conversation">
      {[
        { w: "w-3/4", lines: 2 },
        { w: "w-1/2", lines: 1 },
        { w: "w-2/3", lines: 3 },
      ].map((row, idx) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton, no semantic identity
          key={idx}
          className="flex gap-2.5 px-1.5 py-1"
        >
          <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3 w-24" />
            {Array.from({ length: row.lines }).map((_, i) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton row
                key={i}
                className={cn("h-3", i === row.lines - 1 ? row.w : "w-full")}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ChatRoute() {
  const auth = useAuthState();

  // Chat owns a lot of tenant-scoped local state (agents, session rail,
  // active transcript, in-flight SSE stream, attachments). React Router
  // revalidation updates the auth context, but it does not remount this
  // route because `/chat` itself has no loader. Key by active team so a
  // workspace switch drops old-team state immediately instead of waiting
  // for each child effect to eventually refetch.
  return <ChatSurface key={auth.tenant.team_id} />;
}

function ChatSurface() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [sending, setSending] = useState(false);
  const [openingSession, setOpeningSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const nextId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const skipNextResetRef = useRef(false);
  const [historyReloadKey, setHistoryReloadKey] = useState(0);
  const [optimisticSession, setOptimisticSession] = useState<ChatSession | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAgentsLoading(true);
    const preselected = consumeChatPreselectedAgentId();
    api<AgentListResponse>("/agents?limit=100")
      .then((res) => {
        if (cancelled) return;
        const rows = res.items ?? [];
        setAgents(rows);
        const preselectedRow = preselected !== null ? rows.find((a) => a.id === preselected) : null;
        const preferred =
          preselectedRow ?? rows.find((a) => /^[0-9a-f-]{36}$/i.test(a.id)) ?? rows[0];
        if (preferred) setAgentId(preferred.id);
      })
      .catch((e) => {
        if (!cancelled) setError(`Could not load agents: ${e}`);
      })
      .finally(() => {
        if (!cancelled) setAgentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: agentId is the trigger, setters are stable
  useEffect(() => {
    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      return;
    }
    setSessionId(null);
    setTranscript([]);
  }, [agentId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: transcript is the trigger to scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript]);

  const agent = agents.find((a) => a.id === agentId);

  const startNewSession = (): void => {
    setSessionId(null);
    setTranscript([]);
    setAttachments([]);
    setOptimisticSession(null);
  };

  const openSession = async (session: ChatSession, history: ChatHistoryItem[]): Promise<void> => {
    abortRef.current?.abort();
    abortRef.current = null;
    setError(null);
    setAttachments([]);
    setOptimisticSession(null);

    const entries: TranscriptEntry[] = [];
    for (const item of history) {
      const ts = item.timestamp ? Date.parse(item.timestamp) : Number.NaN;
      const createdAt = Number.isFinite(ts) ? ts : Date.now();
      if (item.role === "user") {
        entries.push({
          id: nextId.current++,
          kind: "user",
          text: item.content,
          speaker: USER_SPEAKER_LABEL,
          createdAt,
          images: imagesFromParts(item.parts ?? []),
        });
      } else if (item.role === "assistant") {
        const bubble = newAgentBubble();
        bubble.text = item.content;
        bubble.images = imagesFromParts(item.parts ?? []);
        const a = agents.find((x) => x.id === session.agent_id);
        const speaker = a?.name || a?.id || "agent";
        entries.push({
          id: nextId.current++,
          kind: "agent",
          bubble,
          speaker,
          createdAt,
        });
      }
    }
    if (entries.length === 0) {
      entries.push({
        id: nextId.current++,
        kind: "system",
        text: "No transcript stored for this session yet.",
        createdAt: Date.now(),
      });
    }

    const threadKey = session.origin.thread_id || session.id;
    if (agentId === session.agent_id) {
      setSessionId(threadKey);
      setTranscript(entries);
    } else {
      skipNextResetRef.current = true;
      setAgentId(session.agent_id);
      setSessionId(threadKey);
      setTranscript(entries);
    }
  };

  const ensureSession = async (): Promise<string | null> => {
    if (sessionId || !agentId) return sessionId;
    if (!agentId) return null;
    try {
      const body: Record<string, unknown> = {
        agent_id: agentId,
        origin: { channel: "web", thread_id: `chat-web-${Date.now()}` },
      };
      const s = await api<{ id: string }>("/sessions", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setSessionId(s.id);
      return s.id;
    } catch (e) {
      setError(`POST /sessions failed: ${String(e)}`);
      return null;
    }
  };

  const uploadFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;
    const sid = await ensureSession();
    if (!sid) return;
    setUploading(true);
    try {
      const res = await apiUpload<ChatUploadResponse>("/kb/chat-upload", files, {
        session_id: sid,
      });
      const newAttachments: ChatAttachment[] = res.uploaded.map((u, i) => ({
        id: nextId.current++,
        name: u.source_path.split("/").pop() ?? u.source_path,
        size: u.bytes,
        bucket: u.bucket,
        source_path: u.source_path,
        absolute_path: u.absolute_path,
        // biome-ignore lint/style/noNonNullAssertion: server returns parts in 1:1 order with uploaded
        part: res.parts[i]!,
      }));
      setAttachments((prev) => [...prev, ...newAttachments]);
    } catch (e) {
      setError(`upload failed: ${String(e)}`);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (id: number): void => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) void uploadFiles(files);
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
    const files: File[] = [];
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.kind === "file") {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      void uploadFiles(files);
    }
  };

  const send = async (): Promise<void> => {
    if (!agentId) return;
    const text = draft.trim();
    if (!text && attachments.length === 0) return;

    const resetCmd = text ? matchSessionResetCommand(text) : undefined;
    if (resetCmd) {
      setDraft("");
      setAttachments([]);
      const prev = sessionId;
      setSessionId(null);
      setTranscript((tt) => [
        ...tt,
        {
          id: nextId.current++,
          kind: "system",
          text: `New session started (${resetCmd}) — prior context dropped`,
          createdAt: Date.now(),
        },
      ]);
      if (prev) {
        try {
          await api(`/sessions/${encodeURIComponent(prev)}/end`, {
            method: "POST",
          });
        } catch {
          /* best-effort */
        }
      }
      return;
    }

    setSending(true);
    setError(null);
    const pendingAttachments = attachments;
    setDraft("");
    setAttachments([]);

    let sid = sessionId;
    const isNewSessionTurn = !sid;
    if (!sid) {
      sid = await ensureSession();
      if (!sid) {
        setSending(false);
        return;
      }
    }
    if (isNewSessionTurn && sid) {
      const now = new Date().toISOString();
      setOptimisticSession({
        id: sid,
        agent_id: agentId,
        title: "Summarizing title…",
        origin: { channel: "web", thread_id: sid },
        created_at: now,
        updated_at: now,
        last_run_status: "running",
      });
      void api<{ title: string }>("/sessions/title-suggestion", {
        method: "POST",
        body: JSON.stringify({ agent_id: agentId, input_text: text }),
      })
        .then((res) => {
          const title = res.title?.trim();
          if (!title) return;
          setOptimisticSession((prev) => (prev?.id === sid ? { ...prev, title } : prev));
        })
        .catch(() => {
          setOptimisticSession((prev) =>
            prev?.id === sid ? { ...prev, title: text.slice(0, 32) || prev.title } : prev,
          );
        });
    }

    const userId = nextId.current++;
    const agentBubbleId = nextId.current++;
    const agentSpeaker = agent?.name || agent?.id || "agent";
    const userBubbleText = [text, ...pendingAttachments.map((a) => `📎 ${a.name}`)]
      .filter(Boolean)
      .join("\n");
    const userImages = imagesFromParts(
      pendingAttachments.map((a) => a.part),
      pendingAttachments.find((a) => a.bucket === "imgs")?.name,
    );
    const turnStartedAt = Date.now();
    setTranscript((tt) => [
      ...tt,
      {
        id: userId,
        kind: "user",
        text: userBubbleText,
        speaker: USER_SPEAKER_LABEL,
        createdAt: turnStartedAt,
        images: userImages,
      },
      {
        id: agentBubbleId,
        kind: "agent",
        bubble: newAgentBubble(),
        speaker: agentSpeaker,
        createdAt: turnStartedAt,
      },
    ]);

    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await runTurn({
        agentId,
        sessionId: sid,
        input: text,
        extraParts: pendingAttachments.map((a) => a.part),
        signal: ac.signal,
        onUpdate: (patch) => {
          setTranscript((tt) =>
            tt.map((entry) => {
              if (entry.id !== agentBubbleId) return entry;
              if (entry.kind !== "agent") return entry;
              return { ...entry, bubble: patch(entry.bubble) };
            }),
          );
        },
      });
    } catch (e) {
      const aborted = (e as Error).name === "AbortError";
      setTranscript((tt) =>
        tt.map((entry) => {
          if (entry.id !== agentBubbleId || entry.kind !== "agent") return entry;
          return {
            ...entry,
            bubble: {
              ...entry.bubble,
              events: [
                ...entry.bubble.events,
                {
                  id: `bubble-error-${Date.now()}`,
                  text: aborted ? "[cancelled]" : `[error] ${String(e)}`,
                  kind: "error" as const,
                },
              ],
              errored: true,
            },
          };
        }),
      );
    } finally {
      setSending(false);
      abortRef.current = null;
      setHistoryReloadKey((k) => k + 1);
      setOptimisticSession((prev) =>
        prev?.id === sid ? { ...prev, last_run_status: "idle" } : prev,
      );
    }
  };

  const cancel = (): void => {
    abortRef.current?.abort();
  };

  const composerDisabled = !agentId;
  const canSend = !!agentId && (draft.trim().length > 0 || attachments.length > 0);
  const agentTitle = agent ? agent.name || agent.id : "No agent selected";

  return (
    <div className="-m-4 flex h-[calc(100vh-2.75rem)] gap-2 bg-background lg:-m-5">
      <ChatSessionsRail
        agents={agents}
        currentAgentId={agentId}
        currentSessionId={sessionId}
        onOpenSession={openSession}
        onNewSession={startNewSession}
        onOpeningChange={(id) => setOpeningSession(id !== null)}
        reloadKey={historyReloadKey}
        optimisticSession={optimisticSession}
      />

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border/70 px-4">
          {agent ? (
            <LetterAvatar seed={agent.id} label={agentTitle} size="sm" />
          ) : agentsLoading ? (
            <Skeleton className="h-5 w-5 shrink-0 rounded-[4px]" />
          ) : (
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] bg-muted text-[10px] text-muted-foreground">
              ?
            </span>
          )}
          <div className="flex min-w-0 flex-1 items-baseline gap-2">
            {agent ? (
              <span className="truncate text-[13px] font-semibold text-foreground">
                @{agentTitle}
              </span>
            ) : agentsLoading ? (
              <Skeleton className="h-3.5 w-32" />
            ) : (
              <span className="truncate text-[13px] font-semibold text-foreground">
                @{agentTitle}
              </span>
            )}
            <span className="hidden text-[10px] uppercase tracking-[0.14em] text-muted-foreground sm:inline">
              direct
            </span>
            {agent ? (
              <span className="hidden truncate text-[11px] text-muted-foreground md:inline">
                · {agent.spec?.runtime ?? "openai-agents"}
                {sessionId ? ` · ${shortSession(sessionId)}` : ""}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            <Select
              value={agentId ?? ""}
              onValueChange={(v) => setAgentId(v || null)}
              disabled={sending || agentsLoading}
            >
              <SelectTrigger className="h-7 w-[200px] border-border/70 text-[12px]">
                {agentsLoading ? (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading agents…
                  </span>
                ) : (
                  <SelectValue placeholder="Pick an agent" />
                )}
              </SelectTrigger>
              <SelectContent>
                {agents.length === 0 && (
                  <SelectItem value="__none__" disabled>
                    (no agents yet)
                  </SelectItem>
                )}
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} · {a.spec?.runtime ?? "openai-agents"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        <ChatPendingApprovalsBanner sessionId={sessionId} />

        <div className="relative min-h-0 flex-1">
          <div ref={scrollRef} className="absolute inset-0 overflow-y-auto px-4 py-3">
            {error && <ErrorBanner className="mb-3">{error}</ErrorBanner>}
            {transcript.length === 0 && !error && !openingSession ? (
              agentsLoading ? (
                <TranscriptSkeleton />
              ) : (
                <EmptyState className="border-0 bg-transparent py-10">
                  <div className="flex flex-col items-center gap-4">
                    <PetCompanion
                      mode="idle"
                      size="lg"
                      message={
                        agent
                          ? `Ready when you are — say hi to ${agent.name}.`
                          : agents.length === 0
                            ? "No agents yet. Configure one to start chatting."
                            : "Pick an agent above and send the first line."
                      }
                    />
                    <p className="max-w-md text-[12px] text-muted-foreground">
                      {agent
                        ? `Send a message to ${agent.name} to start a session.`
                        : agents.length === 0
                          ? "Create an agent in the Agents page to enable chat."
                          : "Use the agent picker in the header to choose someone to chat with."}
                    </p>
                  </div>
                </EmptyState>
              )
            ) : null}

            <div className="flex flex-col">
              {transcript.map((entry, idx) => {
                if (entry.kind === "system") {
                  return (
                    <div
                      key={entry.id}
                      className="my-1 flex items-center gap-2 px-1 py-0.5 text-[11px] text-muted-foreground"
                    >
                      <span className="h-px flex-1 bg-border/60" />
                      <span>{entry.text}</span>
                      <span className="h-px flex-1 bg-border/60" />
                    </div>
                  );
                }

                const prev = transcript[idx - 1];
                const grouped =
                  !!prev &&
                  prev.kind === entry.kind &&
                  "speaker" in prev &&
                  "speaker" in entry &&
                  prev.speaker === entry.speaker;

                const seed = entry.kind === "user" ? `user:${entry.speaker}` : entry.speaker;

                return (
                  <div
                    key={entry.id}
                    className={cn(
                      "group relative flex gap-2.5 rounded-md px-1.5 py-1 hover:bg-muted/40",
                      grouped ? "mt-0" : "mt-2",
                    )}
                  >
                    <div className="w-7 shrink-0 pt-0.5">
                      {!grouped ? (
                        <LetterAvatar seed={seed} label={entry.speaker} size="md" />
                      ) : (
                        <span className="block text-right text-[10px] leading-7 text-muted-foreground/0 group-hover:text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      {!grouped && (
                        <div className="flex items-baseline gap-1.5">
                          <span
                            className={cn(
                              "text-[13px] font-semibold leading-tight",
                              entry.kind === "user"
                                ? "text-yellow-800 dark:text-[hsl(var(--lemon))]"
                                : "text-foreground",
                            )}
                          >
                            {entry.speaker}
                          </span>
                          <span
                            className="text-[10px] font-mono text-muted-foreground"
                            title={new Date(entry.createdAt).toLocaleString()}
                          >
                            {formatTime(entry.createdAt)}
                          </span>
                        </div>
                      )}
                      <div className="text-[13px] leading-relaxed text-foreground">
                        {entry.kind === "user" ? (
                          <>
                            <div className="whitespace-pre-wrap break-words">{entry.text}</div>
                            <ImageStrip images={entry.images} />
                          </>
                        ) : (
                          <>
                            {entry.bubble.reaction && (
                              <div
                                title={entry.bubble.reaction.emoji}
                                className="my-1 inline-block rounded-full border border-[hsl(var(--lemon))]/35 bg-[hsl(var(--lemon))]/15 px-2 py-0.5 text-[11px] text-yellow-800 dark:text-[hsl(var(--lemon))]"
                              >
                                {entry.bubble.reaction.label}
                              </div>
                            )}
                            <ThinkingBlock
                              text={entry.bubble.thinking}
                              active={entry.bubble.thinkingActive}
                            />
                            <ToolCallList items={entry.bubble.toolCalls} />
                            {entry.bubble.events.map((e) => (
                              <div
                                key={e.id}
                                className={cn(
                                  "my-0.5 text-[11px] italic",
                                  e.kind === "error"
                                    ? "text-destructive not-italic"
                                    : "text-muted-foreground",
                                )}
                              >
                                {e.text}
                              </div>
                            ))}
                            {entry.bubble.text ? <ChatMarkdown source={entry.bubble.text} /> : null}
                            <ImageStrip images={entry.bubble.images} excludeAssistant />
                            {(entry.bubble.usage || typeof entry.bubble.elapsedMs === "number") && (
                              <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                                {formatBubbleFooter(entry.bubble)}
                              </div>
                            )}
                            {sending &&
                              idx === transcript.length - 1 &&
                              isBubbleEmpty(entry.bubble) && (
                                <div className="my-1">
                                  <PetCompanion mode="thinking" size="sm" showBubble={false} />
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {openingSession ? (
            <div
              className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-background/55 pt-12 backdrop-blur-[1px]"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-3 py-1.5 text-[12px] text-muted-foreground shadow-sm">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                Loading conversation…
              </div>
            </div>
          ) : null}
        </div>

        <div className="px-3 pb-3">
          <div
            className={cn(
              "rounded-md border border-border/70 bg-background shadow-sm transition",
              dragActive &&
                "outline outline-2 outline-dashed outline-primary/60 outline-offset-[-3px]",
            )}
            onDragOver={(e) => {
              e.preventDefault();
              if (!dragActive) setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            <div className="flex items-center gap-0.5 border-b border-border/60 px-2 py-1">
              {[Bold, Italic, Code, LinkIcon, ListIcon, Smile].map((Icon) => (
                <button
                  key={Icon.displayName ?? Icon.name}
                  type="button"
                  disabled
                  title="Markdown"
                  className="rounded p-1.5 text-muted-foreground/60 transition hover:bg-muted hover:text-foreground disabled:cursor-default"
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>

            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-b border-border/60 px-2.5 py-1.5">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px]"
                  >
                    {a.bucket === "imgs" ? (
                      (() => {
                        const previewUrl = a.part.type === "image_url" ? a.part.url : null;
                        return previewUrl ? (
                          <img
                            src={previewUrl}
                            alt={a.name}
                            className="h-5 w-5 shrink-0 rounded-sm object-cover"
                          />
                        ) : (
                          <ImageIcon className="h-3 w-3 shrink-0" />
                        );
                      })()
                    ) : (
                      <FileText className="h-3 w-3 shrink-0" />
                    )}
                    <span className="truncate" title={a.source_path}>
                      {a.name}
                    </span>
                    <button
                      type="button"
                      className="ml-0.5 rounded text-muted-foreground transition hover:text-foreground"
                      onClick={() => removeAttachment(a.id)}
                      title="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Textarea
              placeholder={
                agent ? `Message @${agentTitle}…` : "Pick an agent above to start chatting."
              }
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!sending) void send();
                }
              }}
              onPaste={handlePaste}
              disabled={composerDisabled || sending}
              className="min-h-[56px] max-h-[200px] resize-none border-0 bg-transparent px-3 py-2 text-[13px] leading-relaxed shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />

            <div className="flex items-center justify-between border-t border-border/60 px-2 py-1">
              <div className="flex items-center gap-1 px-1 text-[10px] text-muted-foreground">
                <kbd className="inline-flex h-4 min-w-4 items-center justify-center rounded border border-border bg-card px-1 font-mono text-[10px]">
                  /
                </kbd>
                <span className="opacity-60">·</span>
                <kbd className="inline-flex h-4 min-w-4 items-center justify-center rounded border border-border bg-card px-1 font-mono text-[10px]">
                  ⌘
                </kbd>
                <span>+</span>
                <kbd className="inline-flex h-4 items-center justify-center rounded border border-border bg-card px-1 font-mono text-[10px]">
                  Enter
                </kbd>
                <span className="ml-0.5">to send</span>
              </div>
              <div className="flex items-center gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    if (files.length > 0) void uploadFiles(files);
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={composerDisabled || uploading || sending}
                  title="Attach files"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                </Button>
                {sending ? (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="h-7 px-2.5"
                    onClick={cancel}
                    title="Stop"
                  >
                    <Square className="h-3 w-3 fill-current" />
                    Stop
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2.5 shadow-sm"
                    onClick={() => void send()}
                    disabled={!canSend}
                    title="Send"
                  >
                    <ArrowUp className="h-3 w-3" />
                    Send
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
